<template>
	<div class="flex flex-col w-full lg:w-auto">
		<div class="mb-10 lg:mb-20">
			<h2 class="mb-2.5 text-3xl lg:text-[44px] lg:leading-none font-heldane">
				{{ blok.heading }}
			</h2>
			<span class="text-sm">{{ blok.supporting_text }}</span>
		</div>

		<form
			class="flex flex-col items-start gap-4 w-full text-sm"
			@submit.prevent="submit"
		>
			<input
				v-model="formData.name"
				type="text"
				class="px-4 w-full h-10 uppercase"
				:placeholder="$t('Name')"
				name="name"
				required
			/>
			<input
				v-model="formData.email"
				type="email"
				class="px-4 w-full h-10 uppercase"
				:placeholder="$t('Email')"
				name="email"
				required
			/>
			<input
				v-model="formData.phone"
				type="tel"
				class="px-4 w-full h-10 uppercase"
				:placeholder="$t('Phone')"
				name="phone"
			/>
			<button class="button-primary min-w-[100px] uppercase" type="submit">
				{{ blok.button_text || $t("Subscribe") }}
			</button>
		</form>
	</div>
</template>

<script setup lang="ts">
import { useToast } from "vue-toast-notification";
import type { BlokNewsletter } from "~/types/storyblok/storyblok";

interface Props {
	blok: BlokNewsletter;
}
const { blok } = defineProps<Props>();
const { t, locale } = useI18n();
const toast = useToast();
const { identify } = useDotdigital();
const { executeRecaptcha } = useRecaptcha();

const formData = ref({
	name: "",
	email: "",
	phone: ""
});

// [BB-171] Klaviyo welcome flows filter on location.country using these exact
// values (per CRM). Keyed on the market locale; "global" is the .com site. An
// unmapped locale sends no country (no flow, rather than a wrong one).
const KLAVIYO_COUNTRY: Record<string, string> = {
	se: "Sweden",
	no: "Norway",
	dk: "Denmark",
	de: "Germany",
	nl: "Netherlands",
	fr: "France",
	uk: "United Kingdom",
	us: "USA",
	global: "International"
};

const submit = async () => {
	if (!formData.value?.email) {
		toast.error(t("E-mail is required."));
		return;
	}
	// Validate phone if present
	if (formData.value.phone) {
		// E.164 regex: starts with +, then 10-15 digits
		const e164 = /^\+[1-9]\d{9,14}$/;
		if (!e164.test(formData.value.phone)) {
			toast.error(
				t("Phone must be in international format, e.g. +46774656558")
			);
			return;
		}
	}
	// Dotdigital: capture entered email + phone (no-op unless enabled + consent).
	identify({
		email: formData.value.email,
		mobileNumber: formData.value.phone || undefined,
		fullName: formData.value.name || undefined
	});
	try {
		const recaptchaToken = await executeRecaptcha("newsletter_subscribe");
		const response = await $fetch("/api/newsletter/subscribe", {
			method: "POST",
			body: {
				email: formData.value.email,
				name: formData.value.name || undefined,
				phone: formData.value.phone || undefined,
				// [BB-171] location (the exact value the Klaviyo flows filter on)
				// + locale for the Klaviyo profile; the server resolves the Norce
				// application from the locale rather than trusting the body.
				country: KLAVIYO_COUNTRY[locale.value] || undefined,
				localeCode: locale.value,
				recaptchaToken
			}
		});
		if (response.error) {
			console.error("Error from server:", response.error);
			if (response.error.includes("409 Conflict")) {
				toast.info(t("This email is already subscribed."));
			} else {
				// The server error carries internals — logged above, never shown.
				toast.error(t("newsletter_subscribe_failed"));
			}
			return;
		} else {
			if (response.info) {
				toast.warning(t(response.info));
			}
			if (response.success) toast.success(t("You have subscribed"));
		}
	} catch (error) {
		console.error("Subscription failed:", error);
		// reCAPTCHA block (403) and the per-IP rate limit (429) get their own
		// actionable message; anything else is a generic failure. Mirrors the
		// base Newsletter.vue handling.
		const status = (error as { statusCode?: number })?.statusCode;
		if (status === 403) {
			toast.error(t("recaptcha_failed"));
		} else if (status === 429) {
			toast.warning(t("back_in_stock.rate_limited"));
		} else {
			toast.error(t("newsletter_subscribe_failed"));
		}
	}
};
</script>
